import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { LogService } from '@app/core/services/log.service';
import { TimePeriod, TimePeriods } from '@app/shared/models/time-period';
import { Client } from '@app/shared/models/client';
import { ClientUserUriBuilderParams, UserService } from '@app/core/services/user.service';
import { forkJoin } from 'rxjs';
import { BaseWithIdList, PaginatedList } from '@app/shared/models/list';
import { ClientUser, ClientUserQueryResult, ClientUserState } from '@app/shared/models/client-user';
import { TranslationEntry } from '@app/shared/models/translatable';
import { SMPLog } from '@app/shared/models/log';
import { DrI18nService } from '@app/core/services/i18n/i18n.service';


@Component({
    templateUrl: './client-activity-log.component.html',
    styleUrls: ['./client-activity-log.component.scss']
})
export class ClientActivityLogComponent extends BaseWithBundleComponent
                                        implements OnInit, OnDestroy {

    protected client: Client;

    public userList: BaseWithIdList<ClientUser>;
    private previousSelectedUserId: string;
    public selectedUserId: string;

    public logCategories: TranslationEntry[];
    private previousSelectedLogCategory: string;
    public selectedLogCategory: string;

    public logs: PaginatedList<SMPLog>;

    public timePeriods: TranslationEntry[];

    private previousSelectedTimePeriod: TimePeriod;
    public selectedTimePeriod: TimePeriod;

    private previousSelectedDate: Date;
    public selectedDate: Date;

    private previousSearchText: string;
    public searchText: string;


    get filterIsModified(): boolean {
        return this.searchText !== this.previousSearchText ||
               this.selectedTimePeriod !== this.previousSelectedTimePeriod ||
               DrI18nService.dateTimeToDateOnlyString(this.selectedDate) !== DrI18nService.dateTimeToDateOnlyString(this.previousSelectedDate) ||
               this.selectedLogCategory !== this.previousSelectedLogCategory ||
               this.selectedUserId !== this.previousSelectedUserId;
    }

    get filterIsDefault(): boolean {
        return !this.searchText &&
               this.selectedTimePeriod === TimePeriod.TODAY &&
               !this.selectedDate &&
               !this.selectedLogCategory &&
               !this.selectedUserId;
    }

    constructor(protected componentServiceBundle: ComponentServiceBundle, protected logService: LogService, protected userService: UserService) {
        super(componentServiceBundle);
        this._idPrefix = 'client_activity_log.';
    }

    public ngOnInit() {
        super.ngOnInit();

        if (this.drI18nService.loadedTransFile) {
            this.initialize();
        } else {
            this.subscriptions.push(
                this.drI18nService.loadedTransFileSubject
                                  .subscribe(result => {
                    if (result) {
                        this.initialize();
                    }
                })
            );
        }
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
    }

    protected initialize(): void {

        const allTranslated = `-- ${this.translateService.instant(marker('common.label.all'))} --`;

        const dropDownEntryAll = {
            key: '',
            trans: allTranslated
        };

        this.timePeriods = TimePeriods.ALL;

        this.client = this.authenticationService.currentUserValue.client;

        this.resetFilters();

        const observables = [
            this.userService.query(new ClientUserUriBuilderParams(this.client)),
            this.logService.getLogCategories(),
            this.logService.getClientLogs()
        ];

        forkJoin(observables).subscribe((results: any) => {
            const users: BaseWithIdList<ClientUser> = (<ClientUserQueryResult>results[0]).users;
            users.items = users.items.filter(user => user.state !== ClientUserState.NOTINVITED && user.state !== ClientUserState.TEMPORARY);
            this.userList = users;
            this.userList.items.unshift(new ClientUser({ id: '', user_code: allTranslated }));

            this.logCategories = results[1];
            this.logCategories.unshift(Object.assign({}, dropDownEntryAll));

            this.logs = results[2];
        }, err => {
            this.handleError(err);
        });
    }

    public switchToSpecific(): void {
        if (this.selectedDate) {
            this.selectedTimePeriod = TimePeriod.SPECIFIC;
        }
    }

    public resetFilters(): void {
        this.previousSearchText = '';
        this.searchText = '';
        this.previousSelectedTimePeriod = TimePeriod.TODAY;
        this.selectedTimePeriod = TimePeriod.TODAY;
        this.previousSelectedDate = undefined;
        this.selectedDate = undefined;
        this.previousSelectedLogCategory = '';
        this.selectedLogCategory = '';
        this.previousSelectedUserId = '';
        this.selectedUserId = '';
    }

    public applyFilters(): void {
        this.previousSearchText = this.searchText;
        this.previousSelectedTimePeriod = this.selectedTimePeriod;
        this.previousSelectedDate = this.selectedDate;
        this.previousSelectedLogCategory = this.selectedLogCategory;
        this.previousSelectedUserId = this.selectedUserId;

        this.logService.getClientLogs(this.selectedLogCategory, this.selectedUserId, this.searchText, this.selectedDate, this.selectedTimePeriod)
                       .subscribe(result => {
            this.logs = result;
        });
    }

}
